import React, { useState, useEffect, useRef } from 'react';
import './Home.scss';
import Footer from './Footer';

// Cloudflare Images - optimized for fast loading
const defaultImage = 'https://imagedelivery.net/4p56SpSLnVwcpSdULsYT1A/af3d0004-da85-4f48-ec18-50f781530700/squarefullpublic';
const terraImage = 'https://imagedelivery.net/4p56SpSLnVwcpSdULsYT1A/7d4b029a-60da-4ca0-2a17-9220e3cbcc00/squarefullpublic';
const fitshotImage = 'https://imagedelivery.net/4p56SpSLnVwcpSdULsYT1A/bb842319-d21b-41da-63e9-13eb021fef00/squarefullpublic';
// const fitshotImage = 'https://imagedelivery.net/4p56SpSLnVwcpSdULsYT1A/9749c04c-1652-4479-7b0e-2c759f801a00/squarefullpublic';
const rappiImage = 'https://imagedelivery.net/4p56SpSLnVwcpSdULsYT1A/79cc0e37-59c1-4fc1-743a-3997c10ea500/squarefullpublic';
const kidsImage = 'https://imagedelivery.net/4p56SpSLnVwcpSdULsYT1A/21edc332-d6f1-43b9-b976-bf0f803c7600/squarefullpublic';

const Home = () => {
  const isDarkMode = true; // Toggle this to switch between light/dark mode
  const showNextVersion = false; // Toggle to show upcoming content
  const debugging = false; // Set to true to show hover zones
  const [currentImage, setCurrentImage] = useState(defaultImage);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileImageIndex, setMobileImageIndex] = useState(0);
  const [initialFadeComplete, setInitialFadeComplete] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  
  const mobileImageSequence = [defaultImage, terraImage, fitshotImage, rappiImage, kidsImage];
  
  // Fade in ONLY on initial load, then remove the class
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialFadeComplete(true);
    }, 1200); // 0.2s delay + 1.0s fade = 1.2s total
    return () => clearTimeout(timer);
  }, []);
  
  const introRef = useRef(null);
  const terraRef = useRef(null);
  const fitshotRef = useRef(null);
  const rappiRef = useRef(null);
  const kidsRef = useRef(null);
  
  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 700);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Tap-to-change image on mobile
  const handleMobileTap = () => {
    if (!isMobile) return;
    const nextIndex = (mobileImageIndex + 1) % mobileImageSequence.length;
    setMobileImageIndex(nextIndex);
    setCurrentImage(mobileImageSequence[nextIndex]);
  };
  
  const handleMouseEnter = (image) => {
    if (!isMobile) setCurrentImage(image);
  };
  
  const handleMouseLeave = () => {
    if (!isMobile) setCurrentImage(defaultImage);
  };
  
  const handleContactClick = (e) => {
    e.preventDefault();
    setShowModal(true);
  };
  
  const handleEmailClick = () => {
    navigator.clipboard.writeText('elliot.seror@gmail.com');
    setEmailCopied(true);
    setTimeout(() => {
      setShowModal(false);
      setEmailCopied(false);
    }, 1500);
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setEmailCopied(false);
  };
  
  const images = {
    default: defaultImage,
    terra: terraImage,
    fitshot: fitshotImage,
    rappi: rappiImage,
    kids: kidsImage
  };
  
  return (
    <div 
      className={`home-container ${isDarkMode ? 'dark-mode' : ''} ${debugging ? 'debug-mode' : ''}`}
      onClick={handleMobileTap}
    >
      <div className="content-wrapper">
        <div>
          <div className="animation-placeholder">
            {Object.entries(images).map(([key, src]) => (
              <img 
                key={key}
                src={src} 
                alt="Elliot" 
                className={`figurine-image ${currentImage === src ? 'active' : ''} ${key === 'default' && !initialFadeComplete ? 'initial-fade' : ''}`}
              />
            ))}
          </div>
          
          <div className="text-content">
          <h1 ref={introRef} data-section="intro">👋 Hi, I'm Elliot, I build and scale products.</h1>

          <p
            ref={terraRef}
            data-section="terra"
            onMouseEnter={() => handleMouseEnter(terraImage)}
            onMouseLeave={handleMouseLeave}
          >
            I'm currently a Staff Product Manager at{' '}
            <a href="https://tryterra.co/" target="_blank" rel="noopener noreferrer">
              Terra API
            </a>, building health data infrastructure.
            {showNextVersion && (
              <> I also{' '}
                <a href="#" target="_blank" rel="noopener noreferrer">
                give talks
                </a>{' '}
                on product management, vibecoding, and tech careers.
              </>
            )}
          </p>
          
          <p
            ref={fitshotRef}
            data-section="fitshot"
            onMouseEnter={() => handleMouseEnter(fitshotImage)}
            onMouseLeave={handleMouseLeave}
          >
            In 2025, I solo built Fitshot, a fitness app that went viral.{' '}
            <a href="https://medium.com/the-fitshot-series" target="_blank" rel="noopener noreferrer" className="fitshot-link">
              Read the full story
            </a>.
          </p>
          
          <p
            ref={rappiRef}
            data-section="rappi"
            onMouseEnter={() => handleMouseEnter(rappiImage)}
            onMouseLeave={handleMouseLeave}
          >
            Previously, I built product at Handshake and Rappi.
          </p>
          
          <p
            ref={kidsRef}
            data-section="kids"
            onMouseEnter={() => handleMouseEnter(kidsImage)}
            onMouseLeave={handleMouseLeave}
          >
            Most importantly, I'm a dad to Myriam and Isaac, two amazing humans.
          </p>
          
          <button onClick={handleContactClick} className="contact-button">
            contact me
          </button>
          </div>
        </div>
      </div>
      
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseModal}>×</button>
            <button 
              className="modal-email" 
              onClick={handleEmailClick}
            >
              {emailCopied ? '✓ copied' : 'elliot.seror@gmail.com'}
            </button>
            {!emailCopied && <p className="modal-hint">(click to copy)</p>}
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default Home;



// previous version, keep for reference:
// 
// 👋 Hi, I'm Elliot, a product guy and builder.
// I'm currently a Staff Product Manager at Terra API, where we're building the data layer powering health apps.

// I spent 2025 solo building Fitshot, a fitness sharing app that went viral. Read about the Fitshot journey here.

// Before that, I was a Product Manager at Handshake and Rappi.

// Most importantly, I'm a dad to Myriam and Isaac, two amazing humans. I live in London.



// MINIMALIST VERSION

// I'm a Staff Product Manager at Terra API, building health data infrastructure. I speak on product management, vibecoding, and tech careers.

// I solo built Fitshot in 2025, a fitness app that went viral, following roles at Handshake and Rappi. Most importantly, I’m a dad to Myriam and Isaac.